import { FindAllDevicesByUserIdUsecase } from '@contexts/user-devices/application';
import { RoleOptions, type User } from '@contexts/users/domain';

import { IconName } from '@components/atoms/icon/models';
import { BreadcrumbConfig } from '@components/molecules/breadcrumbs/models/breadcrumb';
import { DynamicOptionsParams } from '@components/molecules/breadcrumbs/services/breadcrumbs.service';

import { ClientAccessStrategy } from '@shared/services/access-control/strategies/client-access.strategy';

import type { AccessContext, AccessStrategy } from './contract/access-strategy';
import { AdminAccessStrategy } from './strategies/admin-access.strategy';
import { DelegationManagerAccessStrategy } from './strategies/delegation-manager-access.strategy';
import { NoneAccessStrategy } from './strategies/none-access.strategy';
import { OperatorAccessStrategy } from './strategies/operator-access.strategy';
import { RoomStaffAccessStrategy } from './strategies/room-staff-access.strategy';

export type WidgetConfig = {
	id: string;
	label: string;
	labelParams?: Record<string, any>;
	routerLink?: string;
	icon?: IconName;
};
export interface AccessControl {
	setStrategy(user?: User): AccessControl;
	getWidgets(context: AccessContext): WidgetConfig[];
	getBreadcrumbs(context: AccessContext): Promise<[BreadcrumbConfig, DynamicOptionsParams[]]>;
	checkAccess(roles: string[]): boolean;
}

const strategyRoleMap: Readonly<Record<RoleOptions, AccessStrategy>> = {
	[RoleOptions.ADMIN]: new AdminAccessStrategy(),
	[RoleOptions.DELEGATION_MANAGER]: new DelegationManagerAccessStrategy(),
	[RoleOptions.ROOM_STAFF]: new RoomStaffAccessStrategy(),
	[RoleOptions.OPERATOR]: new OperatorAccessStrategy(),
	[RoleOptions.CLIENT]: new ClientAccessStrategy()
};

export const createAccessControl = (): AccessControl => {
	let strategy: AccessStrategy = new NoneAccessStrategy();

	const getStrategy = (user?: User): AccessStrategy =>
		user ? (strategyRoleMap[user.role] ?? new NoneAccessStrategy()) : new NoneAccessStrategy();

	const accessControl: AccessControl = {
		setStrategy: (user?: User): AccessControl => {
			strategy = getStrategy(user);
			return accessControl;
		},

		getWidgets: (context: AccessContext): WidgetConfig[] => strategy.getWidgets(context),

		getBreadcrumbs(
			context: AccessContext
		): Promise<[BreadcrumbConfig, DynamicOptionsParams[]]> {
			return strategy.getBreadcrumbs(context);
		},

		checkAccess: (roles: string[]): boolean => strategy.canAccess(roles)
	};

	return accessControl;
};
